import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import type { Chapter, Crystal, LibraryItem, Source } from "./types";
import {
  artifactsForCrystal,
  type QuoteArtifact,
} from "./quoteArtifacts";
import { useLocale, type Language } from "./locale";
import {
  ArrowIcon,
  AtlasMark,
  BookIcon,
  CloseIcon,
  ExpandIcon,
  FilmIcon,
  MenuIcon,
  MoonIcon,
  SearchIcon,
  SunIcon,
  TypeIcon,
} from "./icons";
import { Link, useAtlasHistory } from "./navigation";
import { openAnalyticsPreferences } from "./analytics";
import {
  ReaderRouteNextStep,
  SavedMaterialsLink,
  SaveMaterialButton,
} from "./ReaderContinuity";

export type Theme = "light" | "dark";
export type ReadingSize = "normal" | "large";
export type LightboxImage = {
  src: string;
  alt: string;
  caption?: string;
  variant?: "artifact";
};

const SIDEBAR_KEY = "atlas2:sidebar:v1";
const useBrowserLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

function slavicPlural(count: number, one: string, few: string, many: string) {
  const absolute = Math.abs(count);
  const lastTwo = absolute % 100;
  if (lastTwo >= 11 && lastTwo <= 14) return many;
  const last = absolute % 10;
  if (last === 1) return one;
  if (last >= 2 && last <= 4) return few;
  return many;
}

const componentCopy = {
  ru: {
    skip: "К содержанию",
    homeAria: "Атлас — на главную",
    brand: "Атлас",
    subtitle: "Карта человеческой реальности",
    openSearch: "Открыть поиск",
    openContents: "Открыть оглавление",
    closeContents: "Закрыть оглавление",
    collapseSidebar: "Свернуть боковую панель",
    expandSidebar: "Развернуть боковую панель",
    navigation: "Основная навигация",
    introduction: "Введение",
    method: "Как устроен Атлас",
    essays: "Статьи и эссе",
    sources: "Источники и споры",
    library: "Читальный зал",
    authors: "Автор проекта",
    search: "Поиск",
    enlarge: "Крупнее",
    normal: "Обычно",
    enlargeAria: "Увеличить текст",
    normalAria: "Обычный размер текста",
    darker: "Темнее",
    lighter: "Светлее",
    darkAria: "Включить тёмную тему",
    lightAria: "Включить светлую тему",
    backToTop: "В начало",
    historyBack: "Вернуться на предыдущую страницу",
    historyBackShort: "Назад",
    languageAria: "Язык Атласа",
    englishLanguage: "Английский язык",
    ukrainianLanguage: "Украинский язык",
    russianLanguage: "Русский язык",
    searchAria: "Поиск по Атласу",
    searchLabel: "Поиск по Атласу",
    searchPlaceholder: "Идея, автор, книга или фильм…",
    closeSearch: "Закрыть поиск",
    searchHint: "Введите хотя бы две буквы. Поиск видит главы, 54 ключевые идеи, источники, книги и фильмы.",
    noMatches: "Совпадений нет. Попробуйте понятие шире: «власть», «риск», «система», «достоинство».",
    searchResultCount: (total: number) => `Найдено ${total} ${slavicPlural(total, "совпадение", "совпадения", "совпадений")}.`,
    firstResults: (shown: number, total: number) => `Показаны первые ${shown} из ${total} совпадений. Уточните запрос, чтобы сузить список.`,
    resultTypes: { chapter: "Глава", idea: "Идея", essay: "Эссе", source: "Источник", book: "Книга", film: "Фильм" },
    openFigure: "Открыть иллюстрацию во весь экран",
    openArtifact: "Рассмотреть артефакт во весь экран",
    inspectFigure: "Рассмотреть",
    lightboxAria: "Полноэкранный просмотр иллюстрации",
    closeImage: "Закрыть изображение",
    mechanism: "Почему это происходит",
    example: "Пример",
    distinguish: "Не перепутайте",
    boundary: "Где идея перестаёт работать",
    situationQuestion: "Вопрос к ситуации",
    ideaSources: "Источники идеи",
    grounds: "Основания и возражения",
    shoulders: "На чьих плечах стоит эта глава",
    sourceCaveat: "Источник здесь не печать истины. Для каждого указано, что именно он проясняет и где его оптика требует ограничения.",
    clarifies: "Что проясняет",
    doesNotProve: "Чего не доказывает",
    verifySource: "Проверить источник и контекст",
    book: "Книга",
    film: "Фильм",
    whyUse: "Зачем обращаться",
    readWithCaution: "Читать или смотреть с оговоркой",
    source: "Источник",
    footer: "не обещает окончательную систему. Его задача — сделать различия видимыми, решения проверяемыми, а пределы знания честными.",
    editorialMethod: "Редакционный метод",
    analyticsSettings: "Настройки аналитики",
  },
  uk: {
    skip: "Перейти до вмісту",
    homeAria: "Перейти на головну сторінку Атласу",
    brand: "Атлас",
    subtitle: "Карта людської реальності",
    openSearch: "Відкрити пошук",
    openContents: "Відкрити зміст Атласу",
    closeContents: "Закрити зміст Атласу",
    collapseSidebar: "Згорнути бічну панель",
    expandSidebar: "Розгорнути бічну панель",
    navigation: "Основна навігація",
    introduction: "Вступ",
    method: "Як побудований Атлас",
    essays: "Статті та есе",
    sources: "Джерела й дискусії",
    library: "Читальна зала",
    authors: "Автор проєкту",
    search: "Пошук",
    enlarge: "Збільшити",
    normal: "Звичайний розмір",
    enlargeAria: "Збільшити текст",
    normalAria: "Повернути звичайний розмір тексту",
    darker: "Темніше",
    lighter: "Світліше",
    darkAria: "Увімкнути темну тему",
    lightAria: "Увімкнути світлу тему",
    backToTop: "До початку",
    historyBack: "Повернутися на попередню сторінку",
    historyBackShort: "Назад",
    languageAria: "Вибір мови Атласу",
    englishLanguage: "Англійська мова",
    ukrainianLanguage: "Українська мова",
    russianLanguage: "Російська мова",
    searchAria: "Пошук в Атласі",
    searchLabel: "Пошук в Атласі",
    searchPlaceholder: "Ідея, автор, книга або фільм…",
    closeSearch: "Закрити пошук",
    searchHint: "Введіть щонайменше дві літери. Пошук охоплює розділи, 54 ключові ідеї, джерела, книги та фільми.",
    noMatches: "Збігів немає. Спробуйте ширше поняття: «влада», «ризик», «система», «гідність».",
    searchResultCount: (total: number) => `Знайдено ${total} ${slavicPlural(total, "збіг", "збіги", "збігів")}.`,
    firstResults: (shown: number, total: number) => `Показано перші ${shown} із ${total} збігів. Уточніть запит, щоб звузити список.`,
    resultTypes: { chapter: "Розділ", idea: "Ідея", essay: "Есе", source: "Джерело", book: "Книга", film: "Фільм" },
    openFigure: "Відкрити ілюстрацію на весь екран",
    openArtifact: "Розглянути артефакт на весь екран",
    inspectFigure: "Розглянути",
    lightboxAria: "Повноекранний перегляд ілюстрації",
    closeImage: "Закрити зображення",
    mechanism: "Чому це відбувається",
    example: "Приклад",
    distinguish: "Не плутайте",
    boundary: "Де ідея перестає працювати",
    situationQuestion: "Запитання до ситуації",
    ideaSources: "Джерела ідеї",
    grounds: "Підстави й заперечення",
    shoulders: "На чиїх плечах стоїть цей розділ",
    sourceCaveat: "Джерело тут не є печаткою істини. Для кожного джерела зазначено, що саме воно прояснює і де пролягають межі його оптики.",
    clarifies: "Що прояснює",
    doesNotProve: "Чого не доводить",
    verifySource: "Перевірити джерело й контекст",
    book: "Книга",
    film: "Фільм",
    whyUse: "Навіщо звертатися",
    readWithCaution: "Читати або дивитися із застереженням",
    source: "Джерело",
    footer: "не обіцяє остаточної системи. Його завдання — зробити розрізнення видимими, рішення — перевірюваними, а межі знання — чесно окресленими.",
    editorialMethod: "Редакційний метод",
    analyticsSettings: "Налаштування аналітики",
  },
  en: {
    skip: "Skip to content",
    homeAria: "Atlas — home",
    brand: "Atlas",
    subtitle: "A Map of Human Reality",
    openSearch: "Open search",
    openContents: "Open table of contents",
    closeContents: "Close table of contents",
    collapseSidebar: "Collapse sidebar",
    expandSidebar: "Expand sidebar",
    navigation: "Main navigation",
    introduction: "Introduction",
    method: "How the Atlas Works",
    essays: "Articles and Essays",
    sources: "Sources and Debates",
    library: "Reading Room",
    authors: "Project Author",
    search: "Search",
    enlarge: "Larger",
    normal: "Normal",
    enlargeAria: "Increase text size",
    normalAria: "Use normal text size",
    darker: "Darker",
    lighter: "Lighter",
    darkAria: "Turn on dark theme",
    lightAria: "Turn on light theme",
    backToTop: "Back to top",
    historyBack: "Return to the previous page",
    historyBackShort: "Back",
    languageAria: "Atlas language",
    englishLanguage: "English language",
    ukrainianLanguage: "Ukrainian language",
    russianLanguage: "Russian language",
    searchAria: "Search the Atlas",
    searchLabel: "Search the Atlas",
    searchPlaceholder: "Idea, author, book, or film…",
    closeSearch: "Close search",
    searchHint: "Enter at least two letters. Search covers every chapter, 54 key ideas, sources, books, and films.",
    noMatches: "No matches. Try a broader idea such as “power,” “risk,” “system,” or “dignity.”",
    searchResultCount: (total: number) => `Found ${total} ${total === 1 ? "match" : "matches"}.`,
    firstResults: (shown: number, total: number) => `Showing the first ${shown} of ${total} matches. Refine the query to narrow the list.`,
    resultTypes: { chapter: "Chapter", idea: "Idea", essay: "Essay", source: "Source", book: "Book", film: "Film" },
    openFigure: "Open illustration full screen",
    openArtifact: "View artifact full screen",
    inspectFigure: "View closely",
    lightboxAria: "Full-screen illustration viewer",
    closeImage: "Close image",
    mechanism: "Why this happens",
    example: "Example",
    distinguish: "Do not confuse this with",
    boundary: "Where the idea stops working",
    situationQuestion: "Question for the situation",
    ideaSources: "Sources for this idea",
    grounds: "Foundations and objections",
    shoulders: "The shoulders this chapter stands on",
    sourceCaveat: "A source is not a seal of truth. Each entry says what it clarifies and where its lens needs to be limited.",
    clarifies: "What it clarifies",
    doesNotProve: "What it does not prove",
    verifySource: "Check the source and context",
    book: "Book",
    film: "Film",
    whyUse: "Why turn to it",
    readWithCaution: "Read or watch with this caution",
    source: "Source",
    footer: "does not promise a final system. Its task is to make distinctions visible, decisions testable, and the limits of knowledge honest.",
    editorialMethod: "Editorial method",
    analyticsSettings: "Analytics settings",
  },
};

function LanguageSwitch({ language, onSetLanguage, compact = false }: { language: Language; onSetLanguage: (language: Language) => void; compact?: boolean }) {
  const c = componentCopy[language];
  return (
    <div className={`language-switch ${compact ? "language-switch--compact" : ""}`} role="group" aria-label={c.languageAria}>
      <button type="button" className={language === "en" ? "is-active" : ""} aria-label={c.englishLanguage} aria-pressed={language === "en"} onClick={() => onSetLanguage("en")}>EN</button>
      <button type="button" className={language === "uk" ? "is-active" : ""} aria-label={c.ukrainianLanguage} aria-pressed={language === "uk"} onClick={() => onSetLanguage("uk")}>UA</button>
      <button type="button" className={language === "ru" ? "is-active" : ""} aria-label={c.russianLanguage} aria-pressed={language === "ru"} onClick={() => onSetLanguage("ru")}>RU</button>
    </div>
  );
}

export function Shell({
  pathname,
  theme,
  readingSize,
  language,
  onSetLanguage,
  onToggleTheme,
  onToggleReadingSize,
  onOpenSearch,
  children,
}: {
  pathname: string;
  theme: Theme;
  readingSize: ReadingSize;
  language: Language;
  onSetLanguage: (language: Language) => void;
  onToggleTheme: () => void;
  onToggleReadingSize: () => void;
  onOpenSearch: () => void;
  children: ReactNode;
}) {
  const { chapters, essays, parts } = useLocale();
  const c = componentCopy[language];
  const { canGoBack, goBack } = useAtlasHistory();
  const [menuOpen, setMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => typeof localStorage !== "undefined" && localStorage.getItem(SIDEBAR_KEY) === "collapsed",
  );
  const [showBackToTop, setShowBackToTop] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const sidebarRef = useRef<HTMLElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const returnFocusAfterClose = useRef(true);

  useEffect(() => {
    let frame = 0;
    const updateBackToTop = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        setShowBackToTop(window.scrollY > Math.max(480, window.innerHeight * .7));
      });
    };
    updateBackToTop();
    window.addEventListener("scroll", updateBackToTop, { passive: true });
    window.addEventListener("resize", updateBackToTop);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", updateBackToTop);
      window.removeEventListener("resize", updateBackToTop);
    };
  }, []);

  useEffect(() => {
    const closeAfterNavigation = () => {
      if (!menuOpen) return;
      returnFocusAfterClose.current = false;
      setMenuOpen(false);
      window.requestAnimationFrame(() => document.getElementById("main-content")?.focus());
    };
    window.addEventListener("popstate", closeAfterNavigation);
    return () => window.removeEventListener("popstate", closeAfterNavigation);
  }, [menuOpen]);

  useEffect(() => {
    document.body.classList.toggle("nav-open", menuOpen);
    return () => document.body.classList.remove("nav-open");
  }, [menuOpen]);

  useBrowserLayoutEffect(() => {
    const sidebarState = sidebarCollapsed ? "collapsed" : "expanded";
    document.documentElement.dataset.sidebar = sidebarState;
    localStorage.setItem(SIDEBAR_KEY, sidebarState);
  }, [sidebarCollapsed]);

  useEffect(() => {
    if (!menuOpen) return;

    const sidebar = sidebarRef.current;
    const menuButton = menuButtonRef.current;
    const main = document.getElementById("main-content");
    const mobileHeader = document.querySelector<HTMLElement>(".mobile-header");
    const desktopQuery = window.matchMedia("(min-width: 981px)");
    const focusableSelector = [
      "a[href]",
      "button:not([disabled])",
      "input:not([disabled])",
      "select:not([disabled])",
      "textarea:not([disabled])",
      '[tabindex]:not([tabindex="-1"])',
    ].join(",");

    main?.setAttribute("inert", "");
    mobileHeader?.setAttribute("inert", "");
    let firstFocusFrame = 0;
    let secondFocusFrame = 0;
    const moveFocusIntoSidebar = () => {
      if (!sidebar) return;
      (closeButtonRef.current ?? sidebar).focus();
      if (!sidebar.contains(document.activeElement)) sidebar.focus();
    };
    firstFocusFrame = window.requestAnimationFrame(() => {
      secondFocusFrame = window.requestAnimationFrame(moveFocusIntoSidebar);
    });
    const focusFallbackTimer = window.setTimeout(() => {
      if (sidebar && !sidebar.contains(document.activeElement)) moveFocusIntoSidebar();
    }, 350);

    const closeForViewportChange = (event: MediaQueryListEvent) => {
      if (!event.matches) return;
      returnFocusAfterClose.current = false;
      setMenuOpen(false);
      window.requestAnimationFrame(() => main?.focus());
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        returnFocusAfterClose.current = true;
        setMenuOpen(false);
        return;
      }
      if (event.key !== "Tab" || !sidebar) return;

      const focusable = Array.from(sidebar.querySelectorAll<HTMLElement>(focusableSelector))
        .filter((element) => element.getClientRects().length > 0);
      const first = focusable[0];
      const last = focusable.at(-1);
      if (!first || !last) {
        event.preventDefault();
        sidebar.focus();
        return;
      }

      const active = document.activeElement;
      if (event.shiftKey && (active === first || !sidebar.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !sidebar.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    desktopQuery.addEventListener("change", closeForViewportChange);
    return () => {
      window.cancelAnimationFrame(firstFocusFrame);
      window.cancelAnimationFrame(secondFocusFrame);
      window.clearTimeout(focusFallbackTimer);
      window.removeEventListener("keydown", handleKeyDown);
      desktopQuery.removeEventListener("change", closeForViewportChange);
      main?.removeAttribute("inert");
      mobileHeader?.removeAttribute("inert");
      if (returnFocusAfterClose.current) {
        window.requestAnimationFrame(() => menuButton?.focus());
      }
    };
  }, [menuOpen]);

  const openMenu = () => {
    returnFocusAfterClose.current = true;
    setMenuOpen(true);
  };

  const closeMenu = () => {
    returnFocusAfterClose.current = true;
    setMenuOpen(false);
  };

  const scrollToTop = () => {
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    window.scrollTo({ top: 0, behavior: reducedMotion ? "auto" : "smooth" });
    window.requestAnimationFrame(() => document.getElementById("main-content")?.focus({ preventScroll: true }));
  };
  const backToTopVisible = showBackToTop && !menuOpen;

  return (
    <div className="atlas-shell">
      <a className="skip-link" href="#main-content">{c.skip}</a>
      <header className="mobile-header">
        {canGoBack && !menuOpen ? (
          <button
            type="button"
            className="history-back history-back--mobile"
            onClick={goBack}
            aria-label={c.historyBack}
          >
            <ArrowIcon />
            <span>{c.historyBackShort}</span>
          </button>
        ) : (
          <Link href="/" className="mobile-brand" aria-label={c.homeAria}>
            <AtlasMark />
            <span>{c.brand}</span>
          </Link>
        )}
        <div className="mobile-actions">
          <LanguageSwitch language={language} onSetLanguage={onSetLanguage} compact />
          <button className="icon-button" onClick={onOpenSearch} aria-label={c.openSearch}><SearchIcon /></button>
          <button
            ref={menuButtonRef}
            className="icon-button"
            onClick={openMenu}
            aria-label={c.openContents}
            aria-expanded={menuOpen}
            aria-controls="atlas-sidebar"
          ><MenuIcon /></button>
        </div>
      </header>

      {menuOpen && <div className="nav-scrim" aria-hidden="true" onClick={closeMenu} />}

      <aside
        ref={sidebarRef}
        id="atlas-sidebar"
        className={`sidebar ${sidebarCollapsed ? "sidebar--collapsed" : ""} ${menuOpen ? "sidebar--open" : ""}`}
        aria-label={c.navigation}
        tabIndex={-1}
      >
        <div className="sidebar-head">
          <Link href="/" className="brand" aria-label={c.homeAria}>
            <AtlasMark />
            <span className="brand-copy">
              <strong>{c.brand}</strong>
              <small>{c.subtitle}</small>
            </span>
          </Link>
          <button
            type="button"
            className="sidebar-collapse-toggle"
            onClick={() => setSidebarCollapsed((collapsed) => !collapsed)}
            aria-label={sidebarCollapsed ? c.expandSidebar : c.collapseSidebar}
            aria-expanded={!sidebarCollapsed}
            aria-controls="atlas-sidebar-content"
          >
            <ArrowIcon />
          </button>
          <button type="button" ref={closeButtonRef} className="sidebar-close icon-button" onClick={closeMenu} aria-label={c.closeContents}><CloseIcon /></button>
        </div>

        <div id="atlas-sidebar-content" className="sidebar-content" hidden={sidebarCollapsed && !menuOpen}>
          {canGoBack && (
            <button
              type="button"
              className="history-back history-back--sidebar"
              onClick={goBack}
              aria-label={c.historyBack}
            >
              <ArrowIcon />
              <span>{c.historyBackShort}</span>
            </button>
          )}

          <nav className="chapter-nav">
            <Link href="/" className={`nav-home ${pathname === "/" ? "is-active" : ""}`} aria-current={pathname === "/" ? "page" : undefined}>{c.introduction}</Link>
            <p id="nav-essays-label" className="nav-home nav-essay-index nav-essay-index-label">{c.essays}</p>
            <ol className="nav-essay-list" aria-labelledby="nav-essays-label">
              {essays.map((essay) => {
                const href = `/essay/${essay.slug}`;
                const isActive = pathname === href || pathname.startsWith(`${href}/`);
                return (
                  <li key={essay.slug}>
                    <Link
                      href={href}
                      className={isActive ? "is-active" : ""}
                      aria-current={pathname === href ? "page" : isActive ? "location" : undefined}
                      title={essay.title}
                    >
                      <span className="nav-essay-number">{essay.number}</span>
                      <span className="nav-essay-title">{essay.title}</span>
                    </Link>
                  </li>
                );
              })}
            </ol>
            <Link
              href="/library"
              className={`nav-home nav-library-index ${pathname === "/library" ? "is-active" : ""}`}
              aria-current={pathname === "/library" ? "page" : undefined}
            >{c.library}</Link>
            {parts.map((part) => (
              <section className="nav-part" key={part.id}>
                <h2><Link href={`/part/${part.id}`} className={pathname === `/part/${part.id}` ? "is-active" : ""}><span>{part.number}</span>{part.title}</Link></h2>
                <ol>
                  {chapters.filter((chapter) => chapter.part === part.id).map((chapter) => {
                    const href = `/chapter/${chapter.slug}`;
                    return (
                      <li key={chapter.slug}>
                        <Link href={href} className={pathname === href ? "is-active" : ""} aria-current={pathname === href ? "page" : undefined}>
                          <span>{chapter.number}</span>{chapter.navTitle}
                        </Link>
                      </li>
                    );
                  })}
                </ol>
              </section>
            ))}
          </nav>

          <div className="sidebar-utility">
            <SavedMaterialsLink />
            <Link href="/method" className={pathname === "/method" ? "is-active" : ""} aria-current={pathname === "/method" ? "page" : undefined}>{c.method}</Link>
            <Link href="/sources" className={pathname === "/sources" ? "is-active" : ""} aria-current={pathname === "/sources" ? "page" : undefined}>{c.sources}</Link>
            <Link href="/authors" className={pathname === "/authors" ? "is-active" : ""} aria-current={pathname === "/authors" ? "page" : undefined}>{c.authors}</Link>
          </div>

          <div className="sidebar-language">
            <LanguageSwitch language={language} onSetLanguage={onSetLanguage} />
          </div>

          <div className="sidebar-controls">
            <button onClick={onOpenSearch}><SearchIcon /><span>{c.search}</span><kbd>/</kbd></button>
            <button onClick={onToggleReadingSize} aria-label={readingSize === "normal" ? c.enlargeAria : c.normalAria}>
              <TypeIcon /><span>{readingSize === "normal" ? c.enlarge : c.normal}</span>
            </button>
            <button onClick={onToggleTheme} aria-label={theme === "light" ? c.darkAria : c.lightAria}>
              {theme === "light" ? <MoonIcon /> : <SunIcon />}
              <span>{theme === "light" ? c.darker : c.lighter}</span>
            </button>
          </div>
        </div>
      </aside>

      <main id="main-content" className="main-content" tabIndex={-1}>{children}</main>
      <button
        type="button"
        className={`back-to-top ${backToTopVisible ? "is-visible" : ""}`}
        onClick={scrollToTop}
        aria-label={c.backToTop}
        aria-hidden={!backToTopVisible}
        tabIndex={backToTopVisible ? 0 : -1}
      >
        <ArrowIcon />
        <span>{c.backToTop}</span>
      </button>
    </div>
  );
}

type SearchResult = {
  id: string;
  type: string;
  title: string;
  excerpt: string;
  href: string;
};

function normalize(value: string, language: Language) {
  const normalized = value.toLocaleLowerCase(language === "ru" ? "ru-RU" : language === "uk" ? "uk-UA" : "en-US");
  return language === "ru" ? normalized.replace(/ё/g, "е") : normalized;
}

export function SearchDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { language, chapters, essays, sources, library } = useLocale();
  const c = componentCopy[language];
  const dialogRef = useRef<HTMLDialogElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      dialog.showModal();
      window.setTimeout(() => inputRef.current?.focus(), 20);
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  const search = useMemo<{ items: SearchResult[]; total: number }>(() => {
    const needle = normalize(query.trim(), language);
    if (needle.length < 2) return { items: [], total: 0 };
    const all: SearchResult[] = [];
    for (const chapter of chapters) {
      const chapterText = normalize([
        chapter.navTitle,
        chapter.title,
        chapter.lead,
        chapter.thesis,
        chapter.stakes,
        ...chapter.distinctions.flatMap((item) => [item.left, item.right, item.meaning]),
        chapter.synthesis.title,
        chapter.synthesis.body,
        chapter.synthesis.tension,
      ].join(" "), language);
      if (chapterText.includes(needle)) all.push({ id: `chapter-${chapter.slug}`, type: c.resultTypes.chapter, title: chapter.title, excerpt: chapter.lead, href: `/chapter/${chapter.slug}` });
      for (const crystal of chapter.crystals) {
        const text = normalize([
          crystal.title,
          crystal.claim,
          crystal.mechanism,
          crystal.example,
          crystal.distinction,
          crystal.boundary,
          crystal.question,
        ].join(" "), language);
        if (text.includes(needle)) all.push({ id: crystal.id, type: c.resultTypes.idea, title: crystal.title, excerpt: crystal.claim, href: `/chapter/${chapter.slug}#crystal-${crystal.id}` });
      }
    }
    for (const source of sources) {
      const text = normalize(`${source.author} ${source.title} ${source.contribution} ${source.caution} ${source.field}`, language);
      if (text.includes(needle)) all.push({ id: source.id, type: c.resultTypes.source, title: `${source.author} — ${source.title}`, excerpt: source.contribution, href: `/sources#source-${source.id}` });
    }
    for (const essay of essays) {
      const text = normalize([
        essay.kicker,
        essay.title,
        essay.lead,
        essay.opening.join(" "),
        essay.sections.map((section) => `${section.title} ${section.paragraphs.join(" ")} ${section.proposition ?? ""}`).join(" "),
        essay.conclusion.title,
        essay.conclusion.paragraphs.join(" "),
        essay.conclusion.question,
      ].join(" "), language);
      if (text.includes(needle)) all.push({ id: essay.slug, type: c.resultTypes.essay, title: essay.title, excerpt: essay.lead, href: `/essay/${essay.slug}` });
    }
    for (const item of library) {
      const text = normalize(`${item.title} ${item.originalTitle ?? ""} ${item.creator} ${item.thesis} ${item.useFor} ${item.caution}`, language);
      if (text.includes(needle)) all.push({ id: item.id, type: item.kind === "book" ? c.resultTypes.book : c.resultTypes.film, title: item.title, excerpt: item.thesis, href: `/library#work-${item.id}` });
    }
    return { items: all.slice(0, 18), total: all.length };
  }, [c, chapters, essays, language, library, query, sources]);

  const close = () => {
    setQuery("");
    onClose();
  };

  return (
    <dialog ref={dialogRef} className="search-dialog" aria-label={c.searchAria} onClose={close} onCancel={(event) => { event.preventDefault(); close(); }}>
      <div className="search-dialog-head">
        <SearchIcon />
        <label className="sr-only" htmlFor="atlas-search">{c.searchLabel}</label>
        <input ref={inputRef} id="atlas-search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder={c.searchPlaceholder} autoComplete="off" />
        <button className="icon-button" onClick={close} aria-label={c.closeSearch}><CloseIcon /></button>
      </div>
      <p className="sr-only search-status" role="status" aria-live="polite" aria-atomic="true">
        {query.trim().length >= 2 ? c.searchResultCount(search.total) : ""}
      </p>
      <div className="search-results">
        {query.trim().length < 2 && <p className="search-hint">{c.searchHint}</p>}
        {query.trim().length >= 2 && search.total === 0 && <p className="search-hint">{c.noMatches}</p>}
        {search.items.length > 0 && (
          <ul className="search-result-list">
            {search.items.map((result) => (
              <li key={`${result.type}-${result.id}`}>
                <Link href={result.href} className="search-result" onClick={close}>
                  <span>{result.type}</span>
                  <strong>{result.title}</strong>
                  <p>{result.excerpt}</p>
                </Link>
              </li>
            ))}
          </ul>
        )}
        {search.total > search.items.length && (
          <p className="search-count-note">{c.firstResults(search.items.length, search.total)}</p>
        )}
      </div>
    </dialog>
  );
}

export function PageBeacon({
  anchorRef,
  image,
  label,
  title,
}: {
  anchorRef: RefObject<Element | null>;
  image: { src: string; width?: number; height?: number };
  label: string;
  title: string;
}) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;

    setVisible(false);
    const mobileViewport = window.matchMedia("(max-width: 980px)");
    let observer: IntersectionObserver | undefined;

    const observeAnchor = () => {
      observer?.disconnect();
      const topOffset = mobileViewport.matches ? 62 : 0;
      observer = new IntersectionObserver(([entry]) => {
        setVisible(!entry.isIntersecting && entry.boundingClientRect.top < topOffset);
      }, {
        root: null,
        rootMargin: `-${topOffset}px 0px 0px 0px`,
        threshold: 0,
      });
      observer.observe(anchor);
    };

    observeAnchor();
    mobileViewport.addEventListener("change", observeAnchor);
    return () => {
      observer?.disconnect();
      mobileViewport.removeEventListener("change", observeAnchor);
    };
  }, [anchorRef, image.src, title]);

  if (typeof document === "undefined") return null;
  return createPortal(
    <div className={`page-beacon${visible ? " is-visible" : ""}`} aria-hidden="true">
      <div className="page-beacon-summary">
        <span className="page-beacon-thumb">
          <img
            src={image.src}
            alt=""
            width={image.width ?? 1672}
            height={image.height ?? 941}
            decoding="async"
          />
        </span>
        <span className="page-beacon-copy">
          <small>{label}</small>
          <strong>{title}</strong>
        </span>
      </div>
    </div>,
    document.body,
  );
}

export function Figure({ src, alt, caption, onOpen, className = "" }: { src: string; alt: string; caption?: string; onOpen: (src: string, alt: string, caption?: string) => void; className?: string }) {
  const { language } = useLocale();
  const c = componentCopy[language];
  return (
    <figure className={`atlas-figure ${className}`}>
      <button className="figure-button" onClick={() => onOpen(src, alt, caption)} aria-label={`${c.openFigure}: ${alt}`}>
        <img src={src} alt={alt} width="1672" height="941" loading="eager" fetchPriority="high" decoding="async" />
        <span className="figure-expand"><ExpandIcon />{c.inspectFigure}</span>
      </button>
      {caption && <figcaption>{caption}</figcaption>}
    </figure>
  );
}

export function ArtifactFigure({
  artifact,
  theme,
  onOpen,
  context,
  className = "",
}: {
  artifact: QuoteArtifact;
  theme: Theme;
  onOpen: (image: LightboxImage) => void;
  context: string;
  className?: string;
}) {
  const { language } = useLocale();
  const c = componentCopy[language];
  const asset = artifact.assets[theme];
  return (
    <figure className={`artifact-figure ${className}`}>
      <button
        type="button"
        className="artifact-button"
        aria-label={`${c.openArtifact}: ${context}`}
        onClick={() => onOpen({ src: asset.fullSrc, alt: context, variant: "artifact" })}
      >
        <img
          src={asset.src}
          alt=""
          width={asset.width}
          height={asset.height}
          loading="lazy"
          decoding="async"
        />
      </button>
    </figure>
  );
}

export function Lightbox({ image, onClose }: { image: LightboxImage | null; onClose: () => void }) {
  const { language } = useLocale();
  const c = componentCopy[language];
  const dialogRef = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (!image) {
      if (dialog.open) dialog.close();
      return;
    }

    if (!dialog.open) dialog.showModal();
    document.documentElement.classList.add("lightbox-open");
    return () => {
      document.documentElement.classList.remove("lightbox-open");
      if (dialog.open) dialog.close();
    };
  }, [image]);
  return (
    <dialog
      ref={dialogRef}
      className={`lightbox${image?.variant === "artifact" ? " lightbox--artifact" : ""}`}
      aria-label={c.lightboxAria}
      onClose={onClose}
      onCancel={(event) => { event.preventDefault(); onClose(); }}
      onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}
    >
      {image && (
        <div className="lightbox-inner">
          <button className="lightbox-close" onClick={onClose} aria-label={c.closeImage}><CloseIcon /></button>
          <img src={image.src} alt={image.alt} />
          {image.variant !== "artifact" && <p>{image.caption ?? image.alt}</p>}
        </div>
      )}
    </dialog>
  );
}

export function SourceChip({ id }: { id: string }) {
  const { sourceMap } = useLocale();
  const source = sourceMap.get(id);
  if (!source) return null;
  return <Link className="source-chip" href={`/sources#source-${source.id}`}><span>{source.author}</span><small>{source.title}</small></Link>;
}

export function CrystalCard({
  chapterSlug,
  crystal,
  index,
  theme,
  onOpenArtifact,
}: {
  chapterSlug: string;
  crystal: Crystal;
  index: number;
  theme: Theme;
  onOpenArtifact: (image: LightboxImage) => void;
}) {
  const { language } = useLocale();
  const c = componentCopy[language];
  const headerArtifacts = artifactsForCrystal(chapterSlug, crystal.id, "afterHeader");
  const mechanismArtifacts = artifactsForCrystal(chapterSlug, crystal.id, "afterMechanism");
  const titleArtifact = headerArtifacts.find((artifact) => artifact.anchor.kind === "crystal" && artifact.anchor.sourceField === "title");
  const claimArtifact = headerArtifacts.find((artifact) => artifact.anchor.kind === "crystal" && artifact.anchor.sourceField === "claim");
  const mechanismArtifact = mechanismArtifacts.find((artifact) => artifact.anchor.kind === "crystal" && artifact.anchor.sourceField === "mechanism");
  return (
    <article className="crystal-card" id={`crystal-${crystal.id}`}>
      <header className={`crystal-head${headerArtifacts.length ? " crystal-head--illustrated" : ""}`}>
        <div className="crystal-meta"><span>{crystal.status}</span><span>{String(index + 1).padStart(2, "0")}</span></div>
        <h2 id={titleArtifact?.id} data-quote-anchor={titleArtifact?.id}>{crystal.title}</h2>
        <p className="crystal-claim" id={claimArtifact?.id} data-quote-anchor={claimArtifact?.id}>{crystal.claim}</p>
        {headerArtifacts.map((artifact) => (
          <ArtifactFigure
            artifact={artifact}
            theme={theme}
            onOpen={onOpenArtifact}
            context={artifact.anchor.kind === "crystal" ? crystal[artifact.anchor.sourceField] : crystal.claim}
            className="artifact-figure--crystal-header"
            key={artifact.id}
          />
        ))}
      </header>
      <div className="crystal-mechanism">
        <h3>{c.mechanism}</h3>
        <p id={mechanismArtifact?.id} data-quote-anchor={mechanismArtifact?.id}>{crystal.mechanism}</p>
        {mechanismArtifacts.map((artifact) => (
          <ArtifactFigure
            artifact={artifact}
            theme={theme}
            onOpen={onOpenArtifact}
            context={artifact.anchor.kind === "crystal" ? crystal[artifact.anchor.sourceField] : crystal.mechanism}
            className="artifact-figure--crystal-mechanism"
            key={artifact.id}
          />
        ))}
      </div>
      <div className="crystal-example">
        <span>{c.example}</span>
        <p>{crystal.example}</p>
      </div>
      <div className="crystal-guards">
        <section>
          <h3>{c.distinguish}</h3>
          <p>{crystal.distinction}</p>
        </section>
        <section>
          <h3>{c.boundary}</h3>
          <p>{crystal.boundary}</p>
        </section>
      </div>
      <blockquote className="decision-question"><span>{c.situationQuestion}</span>{crystal.question}</blockquote>
      <footer className="crystal-sources" aria-label={c.ideaSources}>
        {crystal.sourceIds.map((id) => <SourceChip key={id} id={id} />)}
      </footer>
      <div className="crystal-reader-actions">
        <SaveMaterialButton
          material={{ kind: "crystal", chapterSlug, crystalId: crystal.id }}
          title={crystal.title}
        />
      </div>
      <ReaderRouteNextStep material={{ kind: "crystal", chapterSlug, crystalId: crystal.id }} />
    </article>
  );
}

export function ChapterSources({ chapter }: { chapter: Chapter }) {
  const { language, sourceMap } = useLocale();
  const c = componentCopy[language];
  const ids = [...new Set(chapter.crystals.flatMap((crystal) => crystal.sourceIds))];
  return (
    <section className="chapter-sources" aria-labelledby="chapter-sources-title">
      <div className="section-kicker">{c.grounds}</div>
      <h2 id="chapter-sources-title">{c.shoulders}</h2>
      <p>{c.sourceCaveat}</p>
      <div className="chapter-source-list">
        {ids.map((id) => {
          const source = sourceMap.get(id);
          return source ? <SourceCard key={source.id} source={source} compact /> : null;
        })}
      </div>
    </section>
  );
}

export function SourceCard({ source, compact = false }: { source: Source; compact?: boolean }) {
  const { language } = useLocale();
  const c = componentCopy[language];
  return (
    <article className={`source-card ${compact ? "source-card--compact" : ""}`} id={`source-${source.id}`}>
      <header>
        <span>{source.field} · {source.year}</span>
        <h2>{source.author}</h2>
        <p>{source.title}</p>
      </header>
      <div className="source-card-body">
        <section><h3>{c.clarifies}</h3><p>{source.contribution}</p></section>
        <section><h3>{c.doesNotProve}</h3><p>{source.caution}</p></section>
      </div>
      <a href={source.url} target="_blank" rel="noreferrer" className="source-link">{c.verifySource} <ArrowIcon /></a>
    </article>
  );
}

export function LibraryCard({ item }: { item: LibraryItem }) {
  const { language, chapters } = useLocale();
  const c = componentCopy[language];
  return (
    <article className="library-card" id={`work-${item.id}`}>
      <div className="work-icon" aria-hidden="true">{item.kind === "book" ? <BookIcon /> : <FilmIcon />}</div>
      <div className="work-main">
        <div className="work-meta"><span>{item.kind === "book" ? c.book : c.film}</span><span>{item.year}</span></div>
        <h2>{item.title}</h2>
        {item.originalTitle && <p className="original-title">{item.originalTitle}</p>}
        <p className="work-creator">{item.creator}</p>
        <p className="work-thesis">{item.thesis}</p>
        <div className="work-details">
          <section><h3>{c.whyUse}</h3><p>{item.useFor}</p></section>
          <section><h3>{c.readWithCaution}</h3><p>{item.caution}</p></section>
        </div>
        <div className="work-links">
          {item.chapterSlugs.slice(0, 4).map((slug) => {
            const chapter = chapters.find((entry) => entry.slug === slug);
            return chapter ? <Link key={slug} href={`/chapter/${slug}`}>{chapter.navTitle}</Link> : null;
          })}
          {item.url && <a href={item.url} target="_blank" rel="noreferrer">{c.source} <ArrowIcon /></a>}
        </div>
      </div>
    </article>
  );
}

const editorialBylineCopy = {
  en: { by: "Written and edited by", method: "How the Atlas tests its claims" },
  uk: { by: "Автор і редактор", method: "Як Атлас перевіряє свої твердження" },
  ru: { by: "Автор и редактор", method: "Как Атлас проверяет свои утверждения" },
} as const;

export function EditorialByline() {
  const { language } = useLocale();
  const c = editorialBylineCopy[language];
  return (
    <div className="editorial-byline">
      <span>{c.by}</span>
      <Link href="/authors" rel="author">@wonder2world</Link>
      <Link href="/method">{c.method}</Link>
    </div>
  );
}

export function PageFooter() {
  const { language } = useLocale();
  const c = componentCopy[language];
  return (
    <footer className="page-footer">
      <AtlasMark />
      <p><strong>{c.brand}</strong> {c.footer}</p>
      <div className="footer-actions">
        <Link href="/method">{c.editorialMethod} <ArrowIcon /></Link>
        <button
          type="button"
          onClick={(event) => openAnalyticsPreferences(event.currentTarget)}
        >
          {c.analyticsSettings}
        </button>
      </div>
    </footer>
  );
}
